import { Button, Col, Row, SegmentControl, Text } from 'components';
import { Api, currencies } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { calculateCost } from '../../lib';
import { BackButton, Discount, Total, UserItem } from './components';

const Summary = ({ data, setData, handleBack }) => {
  const [selectedRecurring, setSelectedRecurring] = useState(0),
    [totalValue, setTotalValue] = useState(0),
    [originalPrice, setOriginalPrice] = useState(0),
    { t } = useTranslation(),
    user = useSelector((state) => state.user),
    { isMobile } = useSelector((state) => state.appState),
    dispatch = useDispatch(),
    navigate = useNavigate(),
    { currency, cards } = user.billing,
    athletes = data.filter((x) => x.user.type === 'athlete'),
    teams = data.filter((x) => x.user.type === 'team'),
    foundCurrency = currencies.find(
      (x) => x.value.toLowerCase() === (currency || 'usd').toLowerCase()
    );

  useEffect(() => {
    // Calculate discounted total value
    setTotalValue(
      data.reduce(
        (acc, obj) =>
          acc + calculateCost(obj, currency, obj.type, selectedRecurring ? 'annual' : 'monthly'),
        0
      )
    );

    // Calculate total value
    setOriginalPrice(
      data.reduce((acc, obj) => acc + obj.pricing[currency].price * (selectedRecurring ? 12 : 1), 0)
    );
  }, [data, selectedRecurring]);

  function handleRemove(user, section) {
    setData(data.filter((x) => x.user._id !== user._id));
  }

  async function handleCheckout() {
    if (!cards?.length) {
      return;
    }

    dispatch({ type: 'LOADING_BUTTON', payload: 'navigator' });

    const postData = data.map((x) => ({
      type: x.type,
      recurring: selectedRecurring ? 'annual' : 'monthly',
      subscribed: x.user._id,
      excludeIds: x.excludeIds
    }));

    const res = await Api.post('/subscriptions/subscribe', { data: postData }, 'navigator');
    if (!res) return dispatch({ type: 'LOADING_BUTTON', payload: null });

    const mapped = res.items.map((x) => {
      const found = data.find((y) => y.user._id === x.subscribed);
      if (!found) return null;

      return { ...x, subscribed: found.user };
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: t('Successfully subscribed.') }
    });

    dispatch({
      type: 'SUBSCRIBE',
      payload: { items: mapped, subscribedIds: res.subscribedIds }
    });

    dispatch({
      type: 'FOLLOW_MULTI',
      payload: res.followedIds
    });

    dispatch({ type: 'LOADING_BUTTON', payload: null });
    navigate('/settings?cta=subscription');
  }
  return (
    <Col wid={isMobile ? '382px' : '936px'} noFlex ht="inherit" center start>
      <Col center wid={isMobile ? '382px' : '936px'} noFlex start>
        <Row marg="40px 0 0" pad={isMobile && '0 16px'} center>
          <BackButton handleBack={handleBack} />

          <Text h2 marg="0 0 0 8px">
            {t('subscribe.subscriptionSummary')}
          </Text>
        </Row>
      </Col>

      <Col bg={!isMobile && '#fff'} marg="26px 0 0" maxHt="calc(100% - 64px)" center>
        <Col
          wid={isMobile ? 'calc(100vw - 32px)' : '456px'}
          pad={isMobile ? '0 16px' : '0 240px'}
          scrollY
          center
          centerAll
          start
          bg={isMobile && '#fff'}
        >
          <SegmentControl
            wid={isMobile ? 'calc(100% - 8px)' : '358px'}
            marg={isMobile ? '24px 0 0' : '40px 0 0'}
            options={[t('subscribe.monthly'), t('subscribe.annual')]}
            selectedIdx={selectedRecurring}
            handleSelect={setSelectedRecurring}
            textStyle={{
              b2: true
            }}
            subtitles={['', t('subscribe.off', { amount: 10 })]}
            small
          />

          <Col marg="24px 0 0" noFlex wid={isMobile ? '70%' : '100%'} center>
            <Discount name={t('subscribe.introductoryOffer')} amount={50} upTo />

            {!!selectedRecurring && <Discount name={t('subscribe.annualDiscount')} amount={10} />}
          </Col>

          {!!athletes.length && (
            <Col start marg="12px 0 0" noFlex wid={isMobile ? '70%' : '100%'} center>
              <Text h2>{t('common.athletes')}</Text>

              {athletes.map((item) => (
                <UserItem
                  key={item.user._id + '' + item.type}
                  {...item}
                  foundCurrency={foundCurrency}
                  handleRemove={(user) => handleRemove(user)}
                  recurring={selectedRecurring ? 'annual' : 'monthly'}
                />
              ))}
            </Col>
          )}

          {!!teams.length && (
            <Col start marg="12px 0 0" noFlex wid={isMobile ? '70%' : '100%'} center>
              <Text h2>{t('common.teams')}</Text>

              {teams.map((item) => (
                <UserItem
                  {...item}
                  key={item.user._id + '' + item.type}
                  foundCurrency={foundCurrency}
                  handleRemove={(user) => handleRemove(user)}
                  recurring={selectedRecurring ? 'annual' : 'monthly'}
                />
              ))}
            </Col>
          )}

          <Total
            selected={data}
            setSelected={setData}
            selectedRecurring={selectedRecurring}
            totalValue={totalValue}
            originalPrice={originalPrice}
          />

          <Col centerAll noFlex wid="100%" pad="16px 0">
            <Button
              wid={isMobile ? '100%' : '456px'} // Use 100% width for mobile
              text={t('common.pay', {
                amount: (totalValue / 100).toLocaleString(undefined, {
                  style: 'currency',
                  currency: currency || 'usd'
                })
              })}
              onClick={handleCheckout}
              marg={'10px 0'}
              disabled={totalValue === 0}
            />
          </Col>
        </Col>
      </Col>
    </Col>
  );
};

export default Summary;
